export type Segment = { text: string; kept: boolean };

type NormalizeFn = (url: URL) => string | URL;

/**
 * Applies a normalize function to a raw user input string.
 * Parses the input as a URL (prepending https:// if needed),
 * passes it to the function, and returns the result.
 */
export function parseUrlInput(raw: string): URL | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
}

export function applyNormalize(raw: string, fn: NormalizeFn): string {
  const url = parseUrlInput(raw);
  if (!url) return raw.trim();
  const result = fn(url);
  if (result instanceof URL) {
    return `${result.origin}${result.pathname.replace(/\/+$/, "")}`;
  }
  return result;
}

/**
 * Splits raw input into segments marking which parts will be kept
 * after normalization. Used for visual feedback — dim the discarded parts.
 *
 * Works by finding the normalized result as a substring of the raw input.
 * If the result isn't a substring (e.g. protocol was prepended), no dimming.
 */
export function computeSegments(raw: string, fn: NormalizeFn): Segment[] {
  if (!raw) return [{ text: raw, kept: true }];

  const result = applyNormalize(raw, fn);
  if (result === raw) return [{ text: raw, kept: true }];

  const idx = raw.indexOf(result);
  if (idx === -1) return [{ text: raw, kept: true }];

  const segments: Segment[] = [];
  if (idx > 0) segments.push({ text: raw.slice(0, idx), kept: false });
  segments.push({ text: result, kept: true });
  const after = idx + result.length;
  if (after < raw.length)
    segments.push({ text: raw.slice(after), kept: false });
  return segments;
}
